// File detail drawer — shown when a file is clicked in the grid/list.
//
// Renders metadata, an inline image preview (for image MIME types) and a
// "İndir" button that streams via /admin/files/{id}/content as a blob.

import { useState } from 'react';
import { App, Button, Descriptions, Drawer, Input, Space, Tag, Typography } from 'antd';
import { Download, Save } from 'lucide-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { filesApi, type FileItem } from '@api/files';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { formatBytes, isImageMime } from '../utils';
import { FileThumbnail } from './FileThumbnail';
import { ScanStatusTag } from './ScanStatusTag';

const { Text } = Typography;

interface FileDetailDrawerProps {
  file: FileItem | null;
  open: boolean;
  onClose: () => void;
}

export function FileDetailDrawer({ file, open, onClose }: FileDetailDrawerProps) {
  const { t } = useTranslation('media');
  if (!file) {
    return <Drawer open={open} onClose={onClose} width={420} title={t('detail.title')} />;
  }
  // Keyed on file.id so per-file editable state (alt text, download flag)
  // re-initializes on selection without a sync effect.
  return <FileDetailBody key={file.id} file={file} open={open} onClose={onClose} />;
}

interface FileDetailBodyProps {
  file: FileItem;
  open: boolean;
  onClose: () => void;
}

function FileDetailBody({ file, open, onClose }: FileDetailBodyProps) {
  const { t } = useTranslation('media');
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [altText, setAltText] = useState(file.altText ?? '');

  const saveAltMutation = useMutation({
    mutationFn: () => filesApi.updateMeta(file.id, altText.trim() || null, file.rowVersion),
    onSuccess: async () => {
      void message.success(t('detail.alt.saved'));
      await qc.invalidateQueries({ queryKey: ['files'], refetchType: 'all' });
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const dirtyAlt = altText.trim() !== (file.altText ?? '').trim();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await filesApi.download(file.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const isImage = isImageMime(file.mimeType, file.fileName);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={Math.min(480, window.innerWidth - 32)}
      title={
        <Space direction="vertical" size={0}>
          <Text strong style={{ wordBreak: 'break-all' }}>
            {file.fileName}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {file.mimeType}
          </Text>
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<Download size={14} />}
          loading={downloading}
          onClick={() => void handleDownload()}
          data-testid="file-download"
        >
          {t('detail.download')}
        </Button>
      }
      data-testid="file-detail-drawer"
    >
      <div style={{ display: 'grid', placeItems: 'center', marginBottom: 16 }}>
        <FileThumbnail
          id={file.id}
          fileName={file.fileName}
          mimeType={file.mimeType}
          size={isImage ? 240 : 96}
          lazy={false}
        />
      </div>
      {isImage ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)', marginBottom: 4 }}>
            {t('detail.alt.label')}
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder={t('detail.alt.placeholder')}
              maxLength={300}
              onPressEnter={() => {
                if (dirtyAlt) saveAltMutation.mutate();
              }}
              data-testid="file-alt-input"
            />
            <Button
              type="primary"
              icon={<Save size={14} />}
              loading={saveAltMutation.isPending}
              disabled={!dirtyAlt}
              onClick={() => saveAltMutation.mutate()}
              data-testid="file-alt-save"
            >
              {t('detail.alt.save')}
            </Button>
          </Space.Compact>
          <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)', marginTop: 4 }}>
            {t('detail.alt.hint')}
          </div>
        </div>
      ) : null}
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label={t('detail.labels.size')}>{formatBytes(file.sizeBytes)}</Descriptions.Item>
        <Descriptions.Item label={t('detail.labels.mime')}>{file.mimeType}</Descriptions.Item>
        <Descriptions.Item label={t('detail.labels.scanStatus')}>
          <ScanStatusTag status={file.scanStatus} />
        </Descriptions.Item>
        <Descriptions.Item label={t('detail.labels.storage')}>
          {file.storageProvider === 'S3' ? <Tag color="blue">S3</Tag> : <Tag>{t('detail.storage.local')}</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label={t('detail.labels.uploadedAt')}>
          {file.createdAt ? dayjs(file.createdAt).format('DD MMM YYYY HH:mm') : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('detail.labels.updatedAt')}>
          {file.updatedAt ? dayjs(file.updatedAt).format('DD MMM YYYY HH:mm') : '—'}
        </Descriptions.Item>
        {file.sha256Hash ? (
          <Descriptions.Item label={t('detail.labels.sha256')}>
            <Text code style={{ fontSize: 10, wordBreak: 'break-all' }}>
              {file.sha256Hash}
            </Text>
          </Descriptions.Item>
        ) : null}
        {file.publicUrl ? (
          <Descriptions.Item label={t('detail.labels.publicUrl')}>
            <Text copyable style={{ fontSize: 11, wordBreak: 'break-all' }}>
              {file.publicUrl}
            </Text>
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    </Drawer>
  );
}
